import React, { useState, useEffect, useMemo } from 'react';
import { Loader, AlertCircle, Plus, X, Clock, Pencil, Check } from 'lucide-react';
import axios from 'axios';
import API_BASE from '../api';
import DeclineCoachMatches, { DeclineMatchThankYouScreen } from '../components/DeclineCoachMatches';
import { filterCoachSlotLabelsFromToday } from '../utils/coachSlotDates';
import {
  timezoneAbbrevFromLabel,
  formatPrimaryWeeklyLine,
} from '../utils/scheduleDisplay';
import {
  pickSlotForWeek,
  slotPatternKey,
  typicalTimeFromSlot,
} from '../utils/lessonPlan';
import { parseSlotsFromCalendlyRecords, timezoneLabelToIana } from '../utils/matchCalendlySlots';
import { DateTime } from 'luxon';
import * as confettiModule from 'canvas-confetti';
const confetti = confettiModule.default || confettiModule;

// ── Courses + term dates are now fetched from Airtable via backend.
// Keep a small fallback list so the form still works if the API is temporarily unavailable.

const FALLBACK_PROGRAMS = [
  "The Journal Workshop",
  "Author's Workshop",
  "Writing Competition Workshop",
  "ELA",
  "Journal Bootcamp (Lunar New Year)"
];

const FALLBACK_TERM_DATES = [
  "January 2026",
  "February 2026",
  "March 2026",
  "April 2026",
  "May 2026",
  "June 2026",
  "July 2026",
  "August 2026",
  "September 2026",
  "October 2026",
  "November 2026",
  "December 2026"
];

const MONTH_INDEX = {
  january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
  july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
  jan: 0, feb: 1, mar: 2, apr: 3, jun: 5, jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11
};

function termMonthIndex(name) {
  const k = String(name).toLowerCase();
  return MONTH_INDEX[k];
}

/** Local midnight for calendar day (y, monthIndex 0–11, day). */
function atLocalMidnight(y, monthIndex, day) {
  const dt = new Date(y, monthIndex, day);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

/**
 * Last calendar day covered by a term label. Options fully before "today" (local) are hidden.
 * Formats: "January 2026", "January 24 - February 14, 2026", "February 7-28, 2026"
 */
function getTermDateOptionEndDate(label) {
  if (!label || typeof label !== 'string') return null;
  const raw = label.trim();
  const rhs = raw.includes('=') ? raw.split('=').slice(1).join('=').trim() : '';
  const s = rhs || raw;

  let m = s.match(/^([A-Za-z]+)\s+(\d+)-(\d+),\s*(\d{4})$/);
  if (m) {
    const mi = termMonthIndex(m[1]);
    if (mi === undefined) return null;
    const y = parseInt(m[4], 10);
    const dEnd = parseInt(m[3], 10);
    return atLocalMidnight(y, mi, dEnd);
  }

  m = s.match(/^([A-Za-z]+)\s+(\d+)\s*-\s*([A-Za-z]+)\s+(\d+),\s*(\d{4})$/);
  if (m) {
    const mi2 = termMonthIndex(m[3]);
    if (mi2 === undefined) return null;
    const y = parseInt(m[5], 10);
    const dEnd = parseInt(m[4], 10);
    return atLocalMidnight(y, mi2, dEnd);
  }

  m = s.match(/^([A-Za-z]+)\s+(\d{4})$/);
  if (m) {
    const mi = termMonthIndex(m[1]);
    if (mi === undefined) return null;
    const y = parseInt(m[2], 10);
    const lastDay = new Date(y, mi + 1, 0).getDate();
    return atLocalMidnight(y, mi, lastDay);
  }

  // Month-only (e.g. "August") — treat as the next upcoming instance of that month.
  m = s.match(/^([A-Za-z]+)$/);
  if (m) {
    const mi = termMonthIndex(m[1]);
    if (mi === undefined) return null;
    const now = new Date();
    const y = mi < now.getMonth() ? now.getFullYear() + 1 : now.getFullYear();
    const lastDay = new Date(y, mi + 1, 0).getDate();
    return atLocalMidnight(y, mi, lastDay);
  }

  return null;
}

function sortTermLabelsChronologically(labels) {
  const list = Array.isArray(labels) ? labels.filter(Boolean) : [];
  return [...list].sort((a, b) => {
    const da = getTermDateOptionEndDate(a);
    const db = getTermDateOptionEndDate(b);
    const ta = da ? da.getTime() : Number.POSITIVE_INFINITY;
    const tb = db ? db.getTime() : Number.POSITIVE_INFINITY;
    if (ta !== tb) return ta - tb;
    return String(a).localeCompare(String(b));
  });
}

function isTermDateOptionStillSelectable(label, now = new Date()) {
  const end = getTermDateOptionEndDate(label);
  if (!end) return true;
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return end >= startOfToday;
}

const LEGACY_TIMEZONES = [
  "Eastern Time (ET)",
  "Pacific Time (PT)",
  "Mountain Time (MT)",
  "Central Time (CT)",
  "北京时间",
  "Central European Time (CET)"
];

function getAllTimezoneOptions() {
  let iana = [];
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      iana = Intl.supportedValuesOf('timeZone');
    }
  } catch {
    iana = [];
  }
  const unique = new Set([...LEGACY_TIMEZONES, ...iana]);
  return Array.from(unique);
}

const TIMEZONE_OPTIONS = getAllTimezoneOptions();

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const TIME_SLOTS = [];
for (let h = 6; h <= 21; h++) {
  const start = `${h}:00`;
  const end = `${h + 1}:00`;
  const fmt = (hr) => {
    const period = hr >= 12 ? 'PM' : 'AM';
    const h12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
    return `${h12}:00 ${period}`;
  };
  TIME_SLOTS.push({ start, end, label: `${fmt(h)} – ${fmt(h + 1)}` });
}

function toEmbedUrl(url) {
  if (!url) return '';
  const trimmed = String(url).trim();
  if (trimmed.includes('drive.google.com')) {
    return trimmed.replace('/view', '/preview');
  }
  return trimmed;
}

// ── Lesson Schedule (structured weekly plan) ─────────────────────

function slotLabelHasDate(label) {
  if (!label || typeof label !== 'string') return false;
  return /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/i.test(label)
    && /\s+at\s+\d{1,2}:\d{2}/i.test(label);
}

/** Strip parens note + timezone suffix, but never "AM"/"PM" (also 2-letter uppercase). */
function normalizeLabelForMatch(label) {
  if (!label || typeof label !== 'string') return '';
  return String(label)
    .replace(/\s*\(.*?\)\s*$/, '')
    .replace(/\s+(?!AM\s*$|PM\s*$)[A-Z]{2,5}\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Regex-only day-of-week extractor (Luxon weekday numbering: 1=Mon … 7=Sun). */
function extractLuxonWeekdayFromLabel(label) {
  if (!label || typeof label !== 'string') return null;
  const m = String(label).trim().match(/\b(Sun|Mon|Tue|Wed|Thu|Fri|Sat)/i);
  if (!m) return null;
  const map = { sun: 7, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
  return map[m[1].toLowerCase()] || null;
}

/**
 * Same pipeline the coach card uses (buildTimeSummary): exact → nearby → top 3 of all,
 * filtered to entries that contain a month+date. Returns the first label — this is what
 * lesson 1 must match on the detail view.
 */
function firstCardLabelFromMatch(match) {
  const raw = [
    ...(match?.exact_slots || []),
    ...(match?.nearby_slots || []),
    ...(match?.all_coach_slots || []).slice(0, 3),
  ];
  const seen = new Set();
  for (const label of raw) {
    if (!slotLabelHasDate(label)) continue;
    const key = normalizeLabelForMatch(label);
    if (seen.has(key)) continue;
    seen.add(key);
    return label;
  }
  return null;
}

/** Wall-time parse in the student's zone (not browser local `new Date(y,m,d)`). */
function parseSlotLabelToSlot(label, year, studentTimezoneLabel) {
  if (!label || typeof label !== 'string') return null;
  const clean = label.replace(/\s*\(.*?\)\s*$/, '').trim();
  const m = clean.match(/(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{1,2})\s+at\s+(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!m) return null;
  const [, mo, dy, hr, mn, per] = m;
  const zone = timezoneLabelToIana(studentTimezoneLabel);
  const dt = DateTime.fromFormat(`${mo} ${dy} ${year} ${hr}:${mn} ${per}`, 'MMM d yyyy h:mm a', { zone });
  if (!dt.isValid) return null;
  return {
    date: dt.toJSDate(),
    dayOfWeek: dt.weekday % 7,
    dayName: dt.toFormat('EEEE'),
    hours24: dt.hour,
    minutes: dt.minute,
    timeStr: dt.toFormat('h:mm a'),
    display: clean,
    original: label,
    utc: dt.toUTC().toISO(),
    luxonDT: dt,
  };
}

function getTermDateWindow(termLabel, studentTimezoneLabel) {
  const ianaTz = timezoneLabelToIana(studentTimezoneLabel);
  const monthYear = String(termLabel || '').match(/^(\w+)\s+(\d{4})$/);
  let start = null;
  if (monthYear) {
    start = DateTime.fromFormat(`${monthYear[1]} ${monthYear[2]}`, 'MMMM yyyy', { zone: ianaTz });
    if (!start.isValid) {
      start = DateTime.fromFormat(`${monthYear[1]} ${monthYear[2]}`, 'MMM yyyy', { zone: ianaTz });
    }
  }
  if (!start || !start.isValid) return null;
  let firstSat = start.startOf('month');
  while (firstSat.weekday !== 6) {
    firstSat = firstSat.plus({ days: 1 });
  }
  return {
    start: firstSat.startOf('day'),
    end: firstSat.plus({ weeks: 24 }).endOf('day'),
  };
}

function dateTimeInStudentZone(slot, studentTimezoneLabel) {
  if (!slot) return null;
  const zone = timezoneLabelToIana(studentTimezoneLabel);
  if (slot.utc) {
    const dt = DateTime.fromISO(String(slot.utc).trim(), { setZone: true }).setZone(zone);
    return dt.isValid ? dt : null;
  }
  if (slot.luxonDT && typeof slot.luxonDT.setZone === 'function' && slot.luxonDT.isValid) {
    const dt = slot.luxonDT.setZone(zone);
    return dt.isValid ? dt : null;
  }
  if (slot.date != null) {
    const js = slot.date instanceof Date ? slot.date : new Date(slot.date);
    if (!Number.isNaN(js.getTime())) {
      const dt = DateTime.fromMillis(js.getTime()).setZone(zone);
      return dt.isValid ? dt : null;
    }
  }
  return null;
}

function getWeekMondayKeyFromSlot(slot, studentTimezoneLabel) {
  const dt = dateTimeInStudentZone(slot, studentTimezoneLabel);
  if (!dt || !dt.isValid) return '';
  const daysFromMonday = dt.weekday - 1;
  return dt.minus({ days: daysFromMonday }).startOf('day').toISODate();
}

/** One string from absolute instant so weekday + date always match (fixes mixed toLocaleDateString + dayName). */
function formatLessonSlotLine(slot, studentTimezoneLabel, tzAbbr) {
  const dt = dateTimeInStudentZone(slot, studentTimezoneLabel);
  if (dt) {
    return `${dt.toFormat('EEE, MMM d, yyyy')} at ${dt.toFormat('h:mm a')} ${tzAbbr}`;
  }
  const dn = typeof slot?.dayName === 'string' ? slot.dayName.substring(0, 3) : '';
  return `${dn}, ${slot?.date ? slot.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''} at ${slot?.timeStr || ''} ${tzAbbr}`;
}

function formatLessonSlotPickerLine(slot, studentTimezoneLabel) {
  const dt = dateTimeInStudentZone(slot, studentTimezoneLabel);
  if (dt) {
    return `${dt.toFormat('EEE')} ${dt.toFormat('MMM d, yyyy')} @ ${dt.toFormat('h:mm a')}`;
  }
  const dn = typeof slot?.dayName === 'string' ? slot.dayName.substring(0, 3) : '';
  return `${dn} ${slot?.date ? slot.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''} @ ${slot?.timeStr || ''}`;
}

function buildPreferredDayRankMap(availabilitySlots) {
  if (!Array.isArray(availabilitySlots) || availabilitySlots.length === 0) return null;
  const m = new Map();
  for (const w of availabilitySlots) {
    const day = typeof w?.day === 'string' ? w.day.trim() : '';
    if (day && !m.has(day)) m.set(day, m.size);
  }
  return m.size > 0 ? m : null;
}

function preferredDaySortKey(slot, prefMap) {
  if (!prefMap || !slot) return 999;
  const name = slot.dayName;
  if (typeof name === 'string' && prefMap.has(name)) return prefMap.get(name);
  return 999;
}

function buildLessonPlan(match, termStartDates, studentTimezoneLabel, availabilitySlots) {
  const termFirst = (termStartDates || [])[0] || '';
  const yr = termFirst.match(/\d{4}/);
  const year = yr ? parseInt(yr[0]) : new Date().getFullYear();
  const termWindow = getTermDateWindow(termFirst, studentTimezoneLabel);

  let parsed = parseSlotsFromCalendlyRecords(match, studentTimezoneLabel);

  if (!parsed) {
    const exactRaw = match.exact_slots || [];
    const nearbyRaw = match.nearby_slots || [];
    const allRaw = match.all_coach_slots || [];
    const combined = [...exactRaw, ...nearbyRaw, ...allRaw];
    const seen = new Set();
    const unique = combined.filter(s => {
      const key = s.replace(/\s*\(.*?\)\s*$/, '').trim();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    parsed = unique.map(s => parseSlotLabelToSlot(s, year, studentTimezoneLabel)).filter(Boolean);
    if (parsed.length === 0) return { lessons: [], typicalTime: null };
    parsed.sort((a, b) => a.date - b.date);

    const exactSet = new Set(exactRaw.map(s => s.replace(/\s*\(.*?\)\s*$/, '').trim()));
    const nearbySet = new Set(nearbyRaw.map(s => s.replace(/\s*\(.*?\)\s*$/, '').trim()));
    parsed.forEach(p => {
      const cleanOrig = String(p.original || '').replace(/\s*\(.*?\)\s*$/, '').trim();
      p.isExact = exactSet.has(cleanOrig);
      p.isNearby = nearbySet.has(cleanOrig);
    });
  }
  if (parsed.length === 0) return { lessons: [], typicalTime: null };

  if (termWindow) {
    parsed = parsed.filter((slot) => {
      const dt = dateTimeInStudentZone(slot, studentTimezoneLabel);
      return dt && dt.isValid && dt >= termWindow.start && dt <= termWindow.end;
    });
  }
  if (parsed.length === 0) return { lessons: [], typicalTime: null };

  const weekGroups = {};
  for (const slot of parsed) {
    const key = getWeekMondayKeyFromSlot(slot, studentTimezoneLabel);
    if (!key) continue;
    if (!weekGroups[key]) weekGroups[key] = [];
    weekGroups[key].push(slot);
  }
  const sortedKeys = Object.keys(weekGroups).sort();

  const prefDayMap = buildPreferredDayRankMap(availabilitySlots);

  // ── Anchor lesson 1 to the card's first line ────────────────────
  // The coach card shows the first label from exact → nearby → all_coach_slots[0..3].
  // Without this anchor, the generic sort below can pick a different weekday/time
  // (e.g. a Mon 'all' slot) than what the parent sees on the card (e.g. Thu 9:00 AM
  // exact for Abigail Walker, Wed 9:00 AM exact for Kate ZZ).
  const firstCardLabelRaw = firstCardLabelFromMatch(match);
  const firstCardWeekday = extractLuxonWeekdayFromLabel(firstCardLabelRaw);
  const cardNormalized = normalizeLabelForMatch(firstCardLabelRaw || '');
  const seededFirstSlot = (() => {
    if (!cardNormalized) return null;
    // Match on normalized label (both sides stripped of tz + parens) — exact string match
    // is robust against AM/PM / tz-suffix / "(~Xhr offset)" variations.
    const byLabel = parsed.find((s) => {
      const orig = normalizeLabelForMatch(s?.original || '');
      const disp = normalizeLabelForMatch(s?.display || '');
      return orig === cardNormalized || disp === cardNormalized;
    });
    if (byLabel) return byLabel;
    // Fallback: match by weekday + parsed time from the card label.
    const timeMatch = String(firstCardLabelRaw || '').match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (firstCardWeekday && timeMatch) {
      const h = parseInt(timeMatch[1], 10);
      const m = parseInt(timeMatch[2], 10);
      const per = timeMatch[3].toUpperCase();
      const hours24 = per === 'PM' ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h);
      const sameDowTime = parsed.filter((s) => {
        const dt = dateTimeInStudentZone(s, studentTimezoneLabel);
        return dt && dt.isValid
          && dt.weekday === firstCardWeekday
          && dt.hour === hours24
          && dt.minute === m;
      });
      if (sameDowTime.length > 0) {
        sameDowTime.sort((a, b) => a.date - b.date);
        return sameDowTime[0];
      }
    }
    return null;
  })();

  /** Skip earlier ISO weeks that only have low-tier `all` slots so lesson 1 matches card (exact/nearby on preferred day). */
  function startWeekKeyFromPreferredMatches() {
    const preferredTier = parsed.filter((s) => s.isExact || s.isNearby);
    if (preferredTier.length === 0) return null;
    let pool = preferredTier;
    if (prefDayMap) {
      const onPref = preferredTier.filter((s) => preferredDaySortKey(s, prefDayMap) < 999);
      if (onPref.length > 0) pool = onPref;
    }
    let minKey = null;
    let minMs = Infinity;
    for (const slot of pool) {
      const dt = dateTimeInStudentZone(slot, studentTimezoneLabel);
      if (!dt || !dt.isValid) continue;
      const ms = dt.toMillis();
      if (ms < minMs) {
        minMs = ms;
        minKey = getWeekMondayKeyFromSlot(slot, studentTimezoneLabel);
      }
    }
    return minKey;
  }

  const seededWeekKey = seededFirstSlot
    ? getWeekMondayKeyFromSlot(seededFirstSlot, studentTimezoneLabel)
    : null;
  const startWeekKey = seededWeekKey || startWeekKeyFromPreferredMatches();
  const keysToIterate = startWeekKey
    ? sortedKeys.filter((k) => k >= startWeekKey)
    : sortedKeys;

  function sortByPriority(slots) {
    return [...slots].sort((a, b) => {
      const aRank = a.isExact ? 0 : (a.isNearby ? 1 : 2);
      const bRank = b.isExact ? 0 : (b.isNearby ? 1 : 2);
      if (aRank !== bRank) return aRank - bRank;
      const ap = preferredDaySortKey(a, prefDayMap);
      const bp = preferredDaySortKey(b, prefDayMap);
      if (ap !== bp) return ap - bp;
      return a.date - b.date;
    });
  }

  // Generate a small buffer of future weeks so "skip this week" can postpone
  // the schedule by pulling in the next real Calendly-backed slot.
  const MAX_LESSONS = 24;
  const lessons = [];
  let prevSelected = null;
  let anchorKey = null;

  for (const key of keysToIterate) {
    if (lessons.length >= MAX_LESSONS) break;
    const alternatives = sortByPriority(weekGroups[key]);
    if (alternatives.length === 0) continue;

    // For lesson 1 only: force the pool to the same weekday the card's first line
    // shows, so we can never silently pick a different day (Mon for Abigail/Kate).
    const pickAlternatives = (() => {
      if (lessons.length !== 0 || !firstCardWeekday) return alternatives;
      const sameDow = alternatives.filter((s) => {
        const dt = dateTimeInStudentZone(s, studentTimezoneLabel);
        return dt && dt.isValid && dt.weekday === firstCardWeekday;
      });
      return sameDow.length > 0 ? sameDow : alternatives;
    })();

    let selected;
    if (lessons.length === 0 && seededFirstSlot) {
      selected = alternatives.find((alt) => String(alt?.utc || '') === String(seededFirstSlot?.utc || ''))
        || alternatives.find((alt) => String(alt?.original || '').trim() === String(seededFirstSlot?.original || '').trim())
        || pickSlotForWeek(pickAlternatives, prevSelected, anchorKey, sortByPriority);
    } else {
      selected = pickSlotForWeek(pickAlternatives, prevSelected, anchorKey, sortByPriority);
    }
    if (selected && !anchorKey) {
      anchorKey = slotPatternKey(selected);
    }

    lessons.push({
      weekNum: lessons.length + 1,
      mondayKey: key,
      alternatives,
      selected,
      initialSelectedDisplay: selected ? selected.display : null,
      confirmed: false,
      skipped: false,
      edited: false
    });
    prevSelected = selected;
  }

  const firstSel = lessons[0]?.selected;
  const typicalTime = typicalTimeFromSlot(firstSel);
  return { lessons, typicalTime };
}

function ScheduleChangeConfirmModal({ open, onClose }) {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1.25rem',
      }}
      role="presentation"
      onClick={onClose}
    >
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(15, 23, 42, 0.45)' }} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="schedule-change-title"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 440,
          background: '#fff',
          borderRadius: 14,
          boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          border: '1px solid #e5e7eb',
          padding: '1.35rem 1.35rem 1.1rem',
        }}
      >
        <h2 id="schedule-change-title" style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: '#111827' }}>
          Time updated
        </h2>
        <p style={{ margin: '0.75rem 0 0', fontSize: '0.94rem', lineHeight: 1.55, color: '#4b5563' }}>
          You selected a different time for this lesson. To save your choice and enable the submit button, please tap{' '}
          <strong style={{ color: '#111827' }}>Click to confirm</strong> for this lesson.
        </p>
        <div style={{ marginTop: '1.1rem', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              fontSize: '0.88rem',
              fontWeight: 600,
              borderRadius: 8,
              border: 'none',
              background: '#c1009d',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

function LessonScheduleTable({
  match,
  termStartDates,
  requestId,
  onSubmitted,
  studentTimezone,
  initialDraft,
  onDraftChange,
  availabilitySlots = [],
}) {
  const [lessons, setLessons] = useState([]);
  const [typicalTime, setTypicalTime] = useState(null);
  const [draftHydrated, setDraftHydrated] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [scheduleChangeModalOpen, setScheduleChangeModalOpen] = useState(false);
  const editRef = React.useRef(null);
  const tzLabel = studentTimezone || 'Eastern Time (ET)';
  const tzAbbr = timezoneAbbrevFromLabel(tzLabel);

  React.useEffect(() => {
    function handleClickOutside(e) {
      if (editRef.current && !editRef.current.contains(e.target)) setEditingIdx(null);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  React.useEffect(() => {
    setDraftHydrated(false);
    const { lessons: plan, typicalTime: typical } = buildLessonPlan(
      match,
      termStartDates,
      studentTimezone,
      availabilitySlots
    );
    if (initialDraft && Array.isArray(initialDraft.lessons) && initialDraft.lessons.length > 0) {
      setLessons(initialDraft.lessons);
      setTypicalTime(initialDraft.typicalTime || typical);
    } else {
      setLessons(plan);
      setTypicalTime(typical);
    }
    setDraftHydrated(true);
  }, [match, termStartDates, availabilitySlots]);

  React.useEffect(() => {
    if (!onDraftChange || !draftHydrated) return;
    if (!Array.isArray(lessons) || lessons.length === 0) return;
    onDraftChange({ lessons, typicalTime });
  }, [lessons, typicalTime, onDraftChange, draftHydrated]);

  const updateLesson = (idx, updates) => {
    setLessons(prev => prev.map((l, i) => i === idx ? { ...l, ...updates } : l));
  };

  const visibleLessonsForSubmit = lessons
    .filter((l) => !l.skipped)
    .slice(0, 12);
  const allDone = visibleLessonsForSubmit.length > 0 && visibleLessonsForSubmit.every((l) => l.confirmed);

  const handleSubmit = async () => {
    const confirmedSlots = lessons
      .filter((l) => !l.skipped)
      .slice(0, 12)
      .filter((l) => l.confirmed && l.selected)
      .map((l) => l.selected.original || l.selected.display);
    if (confirmedSlots.length === 0) {
      alert('Please confirm at least one lesson.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_BASE}/api/selection/${requestId}`, {
        coach_name: match.coach_name,
        selected_slots: confirmedSlots,
        selected_slot: confirmedSlots[0] || null,
        calendly_url: match.calendly_scheduling_url || null,
      });
      if (onSubmitted) onSubmitted();
    } catch (err) {
      alert('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (lessons.length === 0) {
    return (
      <div style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: 10, textAlign: 'center' }}>
        <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
          No available time slots found for this coach in the selected term range.
        </p>
      </div>
    );
  }

  const BRAND = '#c1009d';
  const primaryLine = formatPrimaryWeeklyLine(typicalTime);
  const firstVisibleLesson = lessons.find((l) => !l.skipped && l.selected);
  const anchorKey = firstVisibleLesson?.selected ? slotPatternKey(firstVisibleLesson.selected) : null;
  const activeLessons = lessons
    .map((l, i) => ({ ...l, originalIdx: i }))
    .filter((l) => !l.skipped)
    .slice(0, 12);
  const skippedLessons = lessons
    .map((l, i) => ({ ...l, originalIdx: i }))
    .filter((l) => l.skipped);

  const getBoxBg = (lesson, deviatesFromWeekly) => {
    if (lesson.skipped) return '#f9fafb';
    if (lesson.edited) return '#fff5f5';
    if (lesson.confirmed) return '#f0fdf4';
    if (deviatesFromWeekly) return '#fef9c3';
    return '#fff';
  };

  return (
    <div>
      <ScheduleChangeConfirmModal
        open={scheduleChangeModalOpen}
        onClose={() => setScheduleChangeModalOpen(false)}
      />
      <p style={{ color: '#374151', fontSize: '0.9rem', marginBottom: '0.65rem', lineHeight: 1.65 }}>
        Based on your availability, your primary weekly time slot is{' '}
        <strong>{primaryLine || 'the time you selected'}</strong>.
      </p>
      <p style={{ color: '#374151', fontSize: '0.9rem', marginBottom: '0.65rem', lineHeight: 1.65 }}>
        We&apos;ve created a full schedule for your class below. You can edit or confirm each session.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {activeLessons.map((lesson, activeIdx) => {
          const idx = lesson.originalIdx;
          const isEditing = editingIdx === idx;
          const deviatesFromWeekly = !!(anchorKey && lesson.selected && slotPatternKey(lesson.selected) !== anchorKey);
          const boxBg = getBoxBg(lesson, deviatesFromWeekly);
          const boxBorder = deviatesFromWeekly && !lesson.confirmed && !lesson.edited ? '1px solid #eab308' : '1px solid #e5e7eb';
          return (
            <div key={idx} ref={isEditing ? editRef : undefined}>
              <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', marginBottom: '0.35rem', letterSpacing: '0.06em' }}>
                LESSON {activeIdx + 1}
              </p>
              <div
                title={deviatesFromWeekly ? 'This time differs from your usual weekly slot' : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  padding: '0.85rem 1rem',
                  background: boxBg,
                  borderRadius: 8,
                  border: boxBorder,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flex: '1 1 auto', minWidth: 0 }}>
                  {lesson.selected ? (
                    <>
                      <span style={{ fontWeight: 700, fontSize: '0.92rem', color: '#111827' }}>
                        {formatLessonSlotLine(lesson.selected, studentTimezone, tzAbbr)}
                      </span>
                      <button
                        type="button"
                        onClick={() => setEditingIdx(isEditing ? null : idx)}
                        disabled={lesson.skipped}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 4,
                          cursor: lesson.skipped ? 'not-allowed' : 'pointer',
                          color: lesson.skipped ? '#d1d5db' : '#6b7280',
                          flexShrink: 0,
                        }}
                        aria-label="Edit time"
                        title={lesson.skipped ? 'Skipped lesson' : 'Edit time'}
                      >
                        <Pencil size={18} />
                      </button>
                    </>
                  ) : (
                    <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>—</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    if (!lesson.skipped && lesson.selected) {
                      const nextConfirmed = !lesson.confirmed;
                      updateLesson(idx, { confirmed: nextConfirmed });
                    }
                  }}
                  style={{
                    background: lesson.confirmed ? '#16a34a' : '#f3f4f6',
                    border: lesson.confirmed ? 'none' : '1px solid #e5e7eb',
                    borderRadius: 999,
                    padding: '0.35rem 0.75rem',
                    cursor: (lesson.skipped || !lesson.selected) ? 'not-allowed' : 'pointer',
                    opacity: (lesson.skipped || !lesson.selected) ? 0.5 : 1,
                    color: lesson.confirmed ? '#fff' : '#6b7280',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                  aria-label={lesson.confirmed ? 'Confirmed' : 'Confirm'}
                  disabled={lesson.skipped || !lesson.selected}
                >
                  {lesson.confirmed ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.85rem' }}>
                      <Check size={16} strokeWidth={3} />
                      Confirm
                    </span>
                  ) : (
                    <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>Click to confirm</span>
                  )}
                </button>
              </div>

              {isEditing && (
                <div style={{
                  marginTop: 6,
                  background: '#fff',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  zIndex: 50,
                  padding: '0.5rem',
                  maxHeight: 260,
                  overflowY: 'auto',
                }}>
                  {lesson.alternatives.map((alt, ai) => {
                    const isCurrent = lesson.selected && alt.display === lesson.selected.display;
                    const isDiffDay = lesson.selected && alt.dayOfWeek !== lesson.selected.dayOfWeek;
                    const altDeviatesAnchor = !!(anchorKey && slotPatternKey(alt) !== anchorKey);
                    const slotLabel = formatLessonSlotPickerLine(alt, studentTimezone);
                    let pillBg = '#dbeafe'; let pillColor = '#1e40af';
                    if (isCurrent) { pillBg = '#bbf7d0'; pillColor = '#166534'; }
                    else if (isDiffDay) { pillBg = '#fed7aa'; pillColor = '#9a3412'; }
                    if (altDeviatesAnchor && !isCurrent) {
                      pillBg = '#fef9c3';
                      pillColor = '#a16207';
                    }
                    return (
                      <div
                        key={ai}
                        onClick={() => {
                          if (isCurrent) {
                            setEditingIdx(null);
                            return;
                          }
                          const init = lesson.initialSelectedDisplay;
                          const isSystemSlot = init != null && String(alt.display).trim() === String(init).trim();
                          const willBecomeEdited = !isSystemSlot;
                          updateLesson(idx, {
                            selected: alt,
                            skipped: false,
                            confirmed: false,
                            edited: willBecomeEdited
                          });
                          setEditingIdx(null);
                          setScheduleChangeModalOpen(true);
                        }}
                        style={{
                          padding: '0.4rem 0.75rem',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        {isCurrent && <span style={{ color: '#166534', fontWeight: 700, fontSize: '1rem' }}>✓</span>}
                        <span style={{
                          display: 'inline-block',
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          color: pillColor,
                          background: pillBg,
                          padding: '0.3rem 0.75rem',
                          borderRadius: 999,
                          border: isCurrent ? '2px solid #22c55e' : '1px solid transparent',
                        }}>
                          {slotLabel}
                        </span>
                      </div>
                    );
                  })}
                  <div
                    onClick={() => {
                      // Keep selected slot so undo can restore immediately.
                      updateLesson(idx, { skipped: true, confirmed: false, edited: false });
                      setEditingIdx(null);
                    }}
                    style={{ padding: '0.4rem 0.75rem', cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#fef2f2'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <span style={{
                      display: 'inline-block',
                      fontSize: '0.82rem',
                      fontWeight: 700,
                      color: '#fff',
                      background: '#2c5282',
                      padding: '0.3rem 0.75rem',
                      borderRadius: 999,
                    }}>
                      POSTPONE THIS WEEK
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {skippedLessons.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <p style={{ fontSize: '0.75rem', fontWeight: 700, color: '#6b7280', marginBottom: '0.5rem', letterSpacing: '0.06em' }}>
            POSTPONED LESSONS
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {skippedLessons.map((lesson) => (
              <div
                key={`skipped-${lesson.originalIdx}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '0.75rem',
                  padding: '0.75rem 0.9rem',
                  background: '#f9fafb',
                  borderRadius: 8,
                  border: '1px dashed #d1d5db',
                }}
              >
                <span style={{ color: '#6b7280', fontStyle: 'italic', fontSize: '0.9rem' }}>
                  {lesson.selected
                    ? `${formatLessonSlotLine(lesson.selected, studentTimezone, tzAbbr)} (postponed)`
                    : 'Postponed week'}
                </span>
                <button
                  type="button"
                  onClick={() => updateLesson(lesson.originalIdx, { skipped: false })}
                  style={{
                    background: '#eef2ff',
                    color: '#3730a3',
                    border: '1px solid #c7d2fe',
                    borderRadius: 999,
                    padding: '0.25rem 0.7rem',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                  title="Undo postpone"
                >
                  Undo postpone
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="lesson-submit-row" style={{ marginTop: '1.25rem' }}>
        <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>
          {activeLessons.filter(l => l.confirmed).length} of {activeLessons.length} lessons confirmed
          {skippedLessons.length > 0 && `, ${skippedLessons.length} postponed`}
        </span>
        <button
          onClick={handleSubmit}
          disabled={!allDone || submitting}
          style={{
            background: allDone && !submitting ? BRAND : '#d1d5db',
            color: '#fff', border: 'none', borderRadius: 8,
            padding: '0.85rem 2.5rem', fontSize: '1rem', fontWeight: 700,
            cursor: allDone && !submitting ? 'pointer' : 'not-allowed',
          }}
        >
          {submitting ? 'Saving...' : 'SUBMIT'}
        </button>
      </div>
    </div>
  );
}

// ── Match Results ────────────────────────────────────────────────

function MatchResults({ matches: rawMatches, studentName, requestId, onReset, showCelebration, termStartDates, studentTimezone, program, initialAvailabilitySlots }) {
  const [matches, setMatches] = useState([...rawMatches].sort((a, b) => (b.score_pct || 0) - (a.score_pct || 0)));
  const BRAND = '#c1009d';
  const BRAND_LIGHT = '#fdf2fb';
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [declineComplete, setDeclineComplete] = useState(false);
  const [coachDetail, setCoachDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [baselineAvailabilitySlots, setBaselineAvailabilitySlots] = useState(Array.isArray(initialAvailabilitySlots) ? initialAvailabilitySlots : []);
  const [rescheduleSlots, setRescheduleSlots] = useState(Array.isArray(initialAvailabilitySlots) ? initialAvailabilitySlots : []);
  const [baselineTimezone, setBaselineTimezone] = useState(studentTimezone || 'Eastern Time (ET)');
  const [rescheduleTimezone, setRescheduleTimezone] = useState(studentTimezone || 'Eastern Time (ET)');
  const [rescheduling, setRescheduling] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [selectedRescheduleDay, setSelectedRescheduleDay] = useState(null);
  const [scheduleDrafts, setScheduleDrafts] = useState({});

  const hasAvailabilityChanges = React.useMemo(() => {
    const original = Array.isArray(baselineAvailabilitySlots) ? baselineAvailabilitySlots : [];
    const current = Array.isArray(rescheduleSlots) ? rescheduleSlots : [];
    if (original.length !== current.length) return true;
    const key = (s) => `${s.day}__${s.start}__${s.end}`;
    const originalKeys = new Set(original.map(key));
    for (const s of current) {
      if (!originalKeys.has(key(s))) return true;
    }
    return rescheduleTimezone !== baselineTimezone;
  }, [baselineAvailabilitySlots, rescheduleSlots, rescheduleTimezone, baselineTimezone]);

  useEffect(() => {
    setMatches([...rawMatches].sort((a, b) => (b.score_pct || 0) - (a.score_pct || 0)));
  }, [rawMatches]);

  useEffect(() => {
    const slots = Array.isArray(initialAvailabilitySlots) ? initialAvailabilitySlots : [];
    setBaselineAvailabilitySlots(slots);
    setRescheduleSlots(slots);
  }, [initialAvailabilitySlots]);

  useEffect(() => {
    const tz = studentTimezone || 'Eastern Time (ET)';
    setBaselineTimezone(tz);
    setRescheduleTimezone(tz);
  }, [studentTimezone]);

  useEffect(() => {
    if (showCelebration) {
      confetti({ particleCount: 100, spread: 60, origin: { y: 0.7 } });
    }
  }, [showCelebration]);

  // Load CoachDetail (bio + video URL) from Airtable CoachDetail table
  useEffect(() => {
    if (selectedIdx === null) {
      setCoachDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      return;
    }

    const match = matches?.[selectedIdx];
    const coachName = match?.coach_name || '';
    if (!coachName.trim()) {
      setCoachDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchDetail() {
      try {
        setDetailLoading(true);
        setDetailError(null);
        const res = await axios.get(`${API_BASE}/api/coaches/detail`, {
          params: { coach_name: coachName }
        });
        if (cancelled) return;
        if (res.data?.success && res.data?.found) {
          setCoachDetail({
            bio: res.data.bio || '',
            url: res.data.url || ''
          });
        } else {
          setCoachDetail(null);
        }
      } catch (err) {
        if (!cancelled) {
          setCoachDetail(null);
          setDetailError(err.response?.data?.error || err.message);
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    }

    fetchDetail();

    return () => {
      cancelled = true;
    };
  }, [selectedIdx, matches]);

  function formatHourLabel(t) {
    const [h] = String(t || '0:00').split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}${period}`;
  }

  const TIME_SLOTS = [];
  for (let h = 6; h <= 21; h++) {
    const start = `${h}:00`;
    const end = `${h + 1}:00`;
    const fmt = (hr) => {
      const period = hr >= 12 ? 'PM' : 'AM';
      const h12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
      return `${h12}:00 ${period}`;
    };
    TIME_SLOTS.push({ start, end, label: `${fmt(h)} – ${fmt(h + 1)}` });
  }
  const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const handleReschedule = async () => {
    if (!requestId) return;
    if (rescheduleSlots.length < 3) {
      alert('Please select at least 3 one-hour slots.');
      return;
    }
    setRescheduling(true);
    try {
      const res = await axios.post(`${API_BASE}/api/intake/${requestId}/reschedule`, {
        availability_slots: rescheduleSlots,
        timezone: rescheduleTimezone
      });
      const nextMatches = Array.isArray(res.data?.matches) ? res.data.matches : [];
      setMatches([...nextMatches].sort((a, b) => (b.score_pct || 0) - (a.score_pct || 0)));
      setSelectedIdx(null);
      if (Array.isArray(res.data?.availability_slots)) {
        setBaselineAvailabilitySlots(res.data.availability_slots);
        setRescheduleSlots(res.data.availability_slots);
      }
      if (res.data?.timezone) {
        setBaselineTimezone(res.data.timezone);
        setRescheduleTimezone(res.data.timezone);
      }
      setRescheduleOpen(false);
    } catch (err) {
      alert('Failed to reschedule: ' + (err.response?.data?.error || err.message));
    } finally {
      setRescheduling(false);
    }
  };

  const AvailabilityPanel = ({ compact = false }) => (
    <div style={{ marginBottom: compact ? '1rem' : '1.4rem', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem 1rem 0.9rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '0.75rem', color: '#374151', fontSize: '0.88rem' }}>
        <span><strong>Class:</strong> {program || '-'}</span>
        <span><strong>Term:</strong> {(termStartDates || [])[0] || '-'}</span>
      </div>

      <div style={{ marginBottom: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
        {rescheduleSlots.length > 0 ? rescheduleSlots.map((slot, i) => (
          <span key={`${slot.day}-${slot.start}-${i}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#f3e8ff', color: '#6b21a8', padding: '4px 8px', borderRadius: 999, fontSize: '0.74rem', fontWeight: 600 }}>
            <Clock size={12} />
            {slot.day} {formatHourLabel(slot.start)}–{formatHourLabel(slot.end)}
          </span>
        )) : <span style={{ color: '#9ca3af', fontSize: '0.82rem' }}>No availability selected</span>}
      </div>
      {Array.isArray(matches) && matches.length < 3 && (
        <p style={{ margin: '-0.15rem 0 0.7rem', fontSize: '0.8rem', fontStyle: 'italic', color: '#6b7280' }}>
          Not seeing enough options? Try adding more slots to your availability.
        </p>
      )}

      {rescheduleOpen && (
        <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: '0.75rem', marginTop: '0.25rem' }}>
          <div style={{ marginBottom: '0.75rem' }}>
            <SearchableTimezoneSelect
              label="Edit timezone (for rescheduling) *"
              value={rescheduleTimezone}
              onChange={(val) => setRescheduleTimezone(val)}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
            {DAYS.map(day => {
              const count = rescheduleSlots.filter(s => s.day === day).length;
              const active = selectedRescheduleDay === day;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => setSelectedRescheduleDay(active ? null : day)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 8,
                    border: '1px solid #e5e7eb',
                    background: active ? BRAND : (count > 0 ? '#f5f3ff' : '#fff'),
                    color: active ? '#fff' : (count > 0 ? '#6d28d9' : '#374151'),
                    fontSize: '0.78rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  {day.slice(0, 3)} {count > 0 ? `(${count})` : ''}
                </button>
              );
            })}
          </div>

          {selectedRescheduleDay && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.35rem', marginBottom: '0.6rem' }}>
              {TIME_SLOTS.map((ts) => {
                const idx = rescheduleSlots.findIndex(s => s.day === selectedRescheduleDay && s.start === ts.start);
                const selected = idx !== -1;
                return (
                  <button
                    key={`${selectedRescheduleDay}-${ts.start}`}
                    type="button"
                    onClick={() => {
                      if (selected) {
                        setRescheduleSlots(prev => prev.filter((_, i) => i !== idx));
                      } else {
                        setRescheduleSlots(prev => [...prev, { day: selectedRescheduleDay, start: ts.start, end: ts.end }]);
                      }
                    }}
                    style={{
                      padding: '7px 8px',
                      borderRadius: 8,
                      border: `1px solid ${selected ? BRAND : '#d1d5db'}`,
                      background: selected ? BRAND : '#fff',
                      color: selected ? '#fff' : '#374151',
                      fontSize: '0.74rem',
                      cursor: 'pointer'
                    }}
                  >
                    {ts.label}
                  </button>
                );
              })}
            </div>
          )}
          {rescheduleSlots.length < 3 && (
            <p style={{
              marginTop: '0.25rem',
              background: '#fef3c7',
              border: '1px solid #fde68a',
              color: '#92400e',
              borderRadius: 8,
              padding: '0.5rem 0.75rem',
              fontSize: '0.78rem',
              fontWeight: 600,
            }}>
              Please select at least 3 one-hour slots. You currently have {rescheduleSlots.length}.
            </p>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', paddingBottom: '0.15rem' }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setRescheduleOpen(v => !v)}>
          {rescheduleOpen ? 'Hide availability editor' : 'Edit availability'}
        </button>
        {(() => {
          const disabled = rescheduling || rescheduleSlots.length < 3 || !hasAvailabilityChanges;
          return (
            <button
              type="button"
              onClick={handleReschedule}
              disabled={disabled}
              aria-disabled={disabled}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '0.4rem 1rem',
                borderRadius: 999,
                border: 'none',
                fontSize: '0.85rem',
                fontWeight: 600,
                background: disabled ? '#d1d5db' : BRAND,
                color: '#fff',
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {rescheduling && (
                <span
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: '2px solid rgba(255,255,255,0.5)',
                    borderTopColor: '#fff',
                    animation: 'spin 0.8s linear infinite',
                  }}
                />
              )}
              <span>{rescheduling ? 'Rescheduling…' : 'Reschedule'}</span>
            </button>
          );
        })()}
      </div>
    </div>
  );


  function buildTimeSummary(match) {
    const times = [
      ...(match.exact_slots || []),
      ...(match.nearby_slots || []),
      ...(match.all_coach_slots || []).slice(0, 3),
    ];
    const seen = new Set();
    const unique = times.filter(t => {
      const key = t.replace(/\s*\(.*?\)\s*$/, '');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const termYearLabel = (termStartDates || [])[0] || '';
    return filterCoachSlotLabelsFromToday(unique, termYearLabel).slice(0, 3);
  }

  function slotHasDate(label) {
    if (!label || typeof label !== 'string') return false;
    return /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/i.test(label) && /\s+at\s+\d{1,2}:\d{2}/i.test(label);
  }

  function buildAllSlots(match) {
    const all = [
      ...(match.exact_slots || []).map(s => ({ label: s, type: 'exact' })),
      ...(match.nearby_slots || []).map(s => ({ label: s, type: 'nearby' })),
      ...(match.all_coach_slots || []).map(s => ({ label: s, type: 'all' })),
    ].filter(s => slotHasDate(s.label));
    const seen = new Set();
    return all.filter(s => {
      const key = s.label.replace(/\s*\(.*?\)\s*$/, '');
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function scoreBadgeColor(score) {
    const s = Number(score) || 0;
    if (s >= 90) return { bg: '#bbf7d0', text: '#14532d' }; // dark green (green-200 / green-900)
    if (s >= 75) return { bg: '#dcfce7', text: '#166534' }; // medium green (green-100 / green-800)
    if (s >= 60) return { bg: '#ecfccb', text: '#3f6212' }; // light green (lime-100 / lime-800)
    if (s >= 45) return { bg: '#ffedd5', text: '#c2410c' }; // orange (orange-100 / orange-700)
    if (s >= 30) return { bg: '#fef9c3', text: '#a16207' }; // yellow (yellow-100 / yellow-700)
    if (s >= 15) return { bg: '#ffe4e6', text: '#be123c' }; // pink-red (rose-100 / rose-700)
    return { bg: '#f3f4f6', text: '#6b7280' }; // gray
  }

  function submitBtnStyle(brand, enabled) {
    return {
      background: enabled ? brand : '#d1d5db',
      color: '#fff', border: 'none', borderRadius: 8,
      padding: '0.85rem 2.5rem', fontSize: '1rem', fontWeight: 700,
      cursor: enabled ? 'pointer' : 'not-allowed',
    };
  }

  if (declineComplete) {
    return <DeclineMatchThankYouScreen />;
  }

  // ── Completion screen (centered, no content above) ──
  if (selectedIdx !== null && submitted) {
    return (
      <div className="completion-screen match-results-page">
        <div style={{
          textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem',
        }}>
          <img src="/logo-letterly.png" alt="Letterly" style={{ height: 56, marginBottom: '0.25rem' }} />
          <p style={{ fontWeight: 700, color: '#166534', fontSize: '1.5rem', margin: 0 }}>
            Completed
          </p>
          <p style={{ color: '#6b7280', fontSize: '0.95rem', margin: 0, maxWidth: 360 }}>
            Your coach has been selected. You will receive a booking confirmation shortly.
          </p>
        </div>
      </div>
    );
  }

  // ── Detail view for a selected coach ──
  if (selectedIdx !== null) {
    const match = matches[selectedIdx];
    const optionLabel = ['A', 'B', 'C'][selectedIdx] || selectedIdx + 1;
    const effectiveBio = (coachDetail?.bio || match.bio || '').trim();
    const effectiveVideoUrl = toEmbedUrl(coachDetail?.url || match.video_url || '');
    const bio = effectiveBio || 'No bio provided.';
    const hasVideo = !!effectiveVideoUrl;

    return (
      <div className="match-results-page">
        <button
          onClick={() => setSelectedIdx(null)}
          style={{
            background: 'none', border: 'none', color: BRAND, cursor: 'pointer',
            fontSize: '0.85rem', fontWeight: 600, padding: 0, marginBottom: '1.5rem',
          }}
        >
          Back to all options
        </button>

        <p style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 500 }}>Option {optionLabel}</p>
        <h1 className="detail-heading" style={{ fontWeight: 700, color: BRAND, marginBottom: '1.5rem' }}>
          {match.coach_name}
        </h1>

        {/* Video - from Airtable coach profile */}
        <div style={{
          borderRadius: 12, overflow: 'hidden', marginBottom: '2rem',
          background: hasVideo ? '#000' : '#f3f4f6',
          position: 'relative',
          ...(hasVideo ? { paddingBottom: '56.25%' } : { padding: '3rem', minHeight: 200 }),
        }}>
          {hasVideo ? (
            <iframe
              src={effectiveVideoUrl}
              title="Coach intro video"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#9ca3af', fontSize: '0.9rem' }}>
              No intro video available
            </div>
          )}
        </div>

        {/* Bio - from Airtable coach profile */}
        <h3 style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Coach Bio</h3>
        <p style={{ color: '#374151', lineHeight: 1.75, marginBottom: '2.5rem', fontSize: '0.95rem' }}>
          {bio}
        </p>

        <LessonScheduleTable
          match={match}
          termStartDates={termStartDates}
          requestId={requestId}
          studentTimezone={rescheduleTimezone}
          availabilitySlots={rescheduleSlots}
          initialDraft={scheduleDrafts[match.coach_name] || null}
          onDraftChange={(draft) => {
            setScheduleDrafts((prev) => ({ ...prev, [match.coach_name]: draft }));
          }}
          onSubmitted={() => {
            setSubmitted(true);
            confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });
            setTimeout(() => {
              confetti({ particleCount: 80, angle: 60, spread: 55, origin: { x: 0 } });
              confetti({ particleCount: 80, angle: 120, spread: 55, origin: { x: 1 } });
            }, 200);
          }}
        />
      </div>
    );
  }

  // ── Overview: 3 coach suggestion cards ──
  return (
    <div className="match-results-page">
      <p style={{ fontSize: '0.9rem', color: '#374151', fontWeight: 500 }}>
        Coach Profile Suggestions
      </p>
      <h1 className="match-overview-heading" style={{ fontWeight: 700, color: BRAND, marginBottom: '0.75rem' }}>
        {studentName}
      </h1>
      <p style={{ color: '#6b7280', lineHeight: 1.7, marginBottom: '2rem', maxWidth: 600 }}>
        {Array.isArray(matches) && matches.length < 3
          ? 'Here are a few coach options for you to choose from, based on your preferences and schedule needs.'
          : 'Here are three coach options for you to choose from, based on your preferences and schedule needs.'}
        Each profile includes a short bio, an intro video, and a brief explanation of why it may be a good fit.
        Please review the options and select the coach you would like to move forward with.
      </p>

      <AvailabilityPanel />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
        {matches.map((match, i) => {
          const displayTimes = buildTimeSummary(match);
          const badge = scoreBadgeColor(match.score_pct);

          return (
            <div key={i} onClick={() => setSelectedIdx(i)} style={{
              background: '#fff', borderRadius: 12,
              border: `2px solid ${i === 0 ? '#bfdbfe' : '#e5e7eb'}`,
              padding: '1.25rem 1.5rem', cursor: 'pointer', transition: 'all 0.2s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = i === 0 ? '#bfdbfe' : '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div className="coach-card-row">
                <div>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.3rem' }}>
                    {match.coach_name}
                  </h2>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    {displayTimes.length > 0
                      ? displayTimes.join(' or ')
                      : 'Schedule upon selection'}
                  </div>
                </div>
                <span style={{
                  background: badge.bg, color: badge.text, borderRadius: 20,
                  padding: '0.35rem 1rem', fontWeight: 700, fontSize: '0.85rem',
                  border: `1.5px solid ${badge.text}22`, whiteSpace: 'nowrap',
                }}>
                  {match.score_pct}% Match
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <DeclineCoachMatches requestId={requestId} onComplete={() => setDeclineComplete(true)} />
    </div>
  );
}

// ── Single-Select Radio Dropdown (Course Selection) ───────────────

function SingleSelectRadioDropdown({ label, placeholder, options, value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayText = value || placeholder;

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>
        {label}
      </label>
      <div
        onClick={() => setOpen(!open)}
        style={{
          width: '100%', padding: '0.65rem 0.75rem', border: '1px solid var(--gray-300)',
          borderRadius: 8, fontSize: '0.9rem', fontFamily: 'inherit', background: 'white',
          cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          color: value ? '#1f2937' : '#9ca3af',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayText}</span>
        <span style={{ fontSize: '0.7rem', color: '#9ca3af', marginLeft: 8 }}>{open ? '▲' : '▼'}</span>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
          background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)', marginTop: 4,
          maxHeight: 240, overflowY: 'auto',
        }}>
          {options.map((opt, i) => {
            const isSelected = value === opt;
            return (
              <label
                key={i}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.6rem',
                  padding: '0.55rem 0.75rem', cursor: 'pointer', fontSize: '0.88rem',
                  background: isSelected ? '#fdf2fb' : 'transparent',
                  borderBottom: i < options.length - 1 ? '1px solid #f3f4f6' : 'none',
                }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f9fafb'; }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; else e.currentTarget.style.background = '#fdf2fb'; }}
              >
                <input
                  type="radio"
                  name={label.replace(/\s/g, '-')}
                  checked={isSelected}
                  onChange={() => { onChange(opt); setOpen(false); }}
                  style={{ accentColor: 'var(--primary)', width: 16, height: 16 }}
                />
                <span style={{ color: isSelected ? 'var(--primary)' : '#374151', fontWeight: isSelected ? 600 : 400 }}>
                  {opt}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Searchable Timezone Select ──────────────────────────────────────
function SearchableTimezoneSelect({ label, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const normalizedQuery = query.trim().toLowerCase();
  const filtered = React.useMemo(() => {
    if (!normalizedQuery) return TIMEZONE_OPTIONS;
    return TIMEZONE_OPTIONS.filter((tz) =>
      tz.toLowerCase().includes(normalizedQuery)
    );
  }, [normalizedQuery]);

  const displayText = value || 'Select timezone...';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>
        {label}
      </label>

      <div
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: '100%',
          padding: '0.65rem 0.75rem',
          border: '1px solid var(--gray-300)',
          borderRadius: 8,
          fontSize: '0.9rem',
          fontFamily: 'inherit',
          background: 'white',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          color: value ? '#1f2937' : '#9ca3af',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayText}
        </span>
        <span style={{ fontSize: '0.7rem', color: '#9ca3af', marginLeft: 8 }}>
          {open ? '▲' : '▼'}
        </span>
      </div>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 50,
            background: '#fff',
            border: '1px solid var(--gray-200)',
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
            marginTop: 4,
            maxHeight: 260,
            display: 'flex',
          flexDirection: 'column',
          }}
        >
          <div style={{ padding: '0.4rem 0.6rem', borderBottom: '1px solid #f3f4f6' }}>
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search timezone…"
              style={{
                width: '100%',
                padding: '0.4rem 0.55rem',
                borderRadius: 6,
                border: '1px solid var(--gray-300)',
                fontSize: '0.85rem',
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div style={{ overflowY: 'auto' }}>
            {filtered.length === 0 && (
              <div
                style={{
                  padding: '0.6rem 0.75rem',
                  fontSize: '0.85rem',
                  color: '#9ca3af',
                }}
              >
                No results. Try another search.
              </div>
            )}
            {filtered.map((tz, i) => {
              const isSelected = value === tz;
              return (
                <button
                  key={`${tz}-${i}`}
                  type="button"
                  onClick={() => {
                    onChange(tz);
                    setOpen(false);
                  }}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.55rem 0.75rem',
                    border: 'none',
                    background: isSelected ? '#fdf2fb' : 'transparent',
                    cursor: 'pointer',
                    fontSize: '0.88rem',
                    color: isSelected ? 'var(--primary)' : '#374151',
                    fontWeight: isSelected ? 600 : 400,
                    borderBottom: '1px solid #f3f4f6',
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.background = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = isSelected ? '#fdf2fb' : 'transparent';
                  }}
                >
                  {tz}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Availability Picker ──────────────────────────────────────────

function AvailabilityPicker({ slots, onChange }) {
  const [selectedDay, setSelectedDay] = useState(null);

  const addSlot = (day, timeSlot) => {
    const exists = slots.some(s => s.day === day && s.start === timeSlot.start);
    if (exists) return;
    onChange([...slots, { day, start: timeSlot.start, end: timeSlot.end }]);
  };

  const removeSlot = (index) => {
    onChange(slots.filter((_, i) => i !== index));
  };

  const grouped = {};
  slots.forEach((s, i) => {
    if (!grouped[s.day]) grouped[s.day] = [];
    grouped[s.day].push({ ...s, index: i });
  });

  return (
    <div>
      <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.9rem' }}>
        Availability *
      </label>
      <p style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginBottom: '0.75rem' }}>
        Select at least 3 one-hour slots during the week.
      </p>

      {slots.length > 0 && (
        <div style={{ marginBottom: '1rem', display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
          {slots.map((slot, i) => (
            <span key={i} style={{
              display: 'inline-flex', alignItems: 'center', gap: '4px',
              background: 'var(--primary)', color: 'white', padding: '4px 10px',
              borderRadius: 20, fontSize: '0.75rem', fontWeight: 500,
            }}>
              <Clock size={12} />
              {slot.day} {formatTime(slot.start)}–{formatTime(slot.end)}
              <button
                type="button"
                onClick={() => removeSlot(i)}
                style={{
                  background: 'none', border: 'none', color: 'white',
                  cursor: 'pointer', padding: '0 0 0 2px', display: 'flex',
                }}
              >
                <X size={14} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
        {DAYS.map(day => {
          const count = (grouped[day] || []).length;
          const isActive = selectedDay === day;
          return (
            <button
              key={day}
              type="button"
              onClick={() => setSelectedDay(isActive ? null : day)}
              style={{
                padding: '6px 12px', borderRadius: 8, fontSize: '0.8rem',
                fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s',
                background: isActive ? 'var(--primary)' : count > 0 ? '#eef2ff' : 'var(--gray-100)',
                color: isActive ? 'white' : count > 0 ? 'var(--primary)' : 'var(--gray-600)',
                border: isActive ? '2px solid var(--primary)' : '2px solid transparent',
              }}
            >
              {day.substring(0, 3)} {count > 0 && `(${count})`}
            </button>
          );
        })}
      </div>

      {selectedDay && (
        <div style={{
          background: 'var(--gray-50)', borderRadius: 12, padding: '1rem',
          border: '1px solid var(--gray-200)',
        }}>
          <p style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '0.75rem' }}>
            Select time slots for {selectedDay}:
          </p>
          <div className="availability-time-grid">
            {TIME_SLOTS.map((ts) => {
              const isSelected = slots.some(s => s.day === selectedDay && s.start === ts.start);
              return (
                <button
                  key={ts.start}
                  type="button"
                  onClick={() => isSelected
                    ? removeSlot(slots.findIndex(s => s.day === selectedDay && s.start === ts.start))
                    : addSlot(selectedDay, ts)
                  }
                  style={{
                    padding: '8px', borderRadius: 8, fontSize: '0.8rem',
                    fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
                    background: isSelected ? 'var(--primary)' : 'white',
                    color: isSelected ? 'white' : 'var(--gray-700)',
                    border: `1px solid ${isSelected ? 'var(--primary)' : 'var(--gray-300)'}`,
                  }}
                >
                  {ts.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {slots.length > 0 && slots.length < 3 && (
        <p style={{ color: 'var(--error)', fontSize: '0.8rem', marginTop: '0.5rem', fontWeight: 500 }}>
          {slots.length} slot{slots.length !== 1 ? 's' : ''} selected. Please select at least {3 - slots.length} more.
        </p>
      )}
      {slots.length >= 3 && (
        <p style={{ color: 'var(--success)', fontSize: '0.8rem', marginTop: '0.5rem', fontWeight: 500 }}>
          {slots.length} slots selected
        </p>
      )}
    </div>
  );
}

function formatTime(t) {
  const [h] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}${period}`;
}

// ── Main Intake Form ─────────────────────────────────────────────

export default function IntakeForm() {
  const [status, setStatus] = useState('idle');
  const [matches, setMatches] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [error, setError] = useState(null);

  const [programOptions, setProgramOptions] = useState(FALLBACK_PROGRAMS);
  const [termDateOptions, setTermDateOptions] = useState(sortTermLabelsChronologically(FALLBACK_TERM_DATES));

  const [form, setForm] = useState({
    student_name: '',
    parent_name: '',
    parent_email: '',
    program: '',
    term_start_date: '',
    timezone: '',
    availability_slots: [],
    comments: ''
  });

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Load Courses + Term Dates from Airtable (backend).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // This route proxies Airtable lookups (Courses + Term Dates).
        const res = await axios.get(`${API_BASE}/api/lookups`);
        const courses = Array.isArray(res.data?.courses) ? res.data.courses.map((c) => c?.name).filter(Boolean) : [];
        const terms = Array.isArray(res.data?.terms) ? res.data.terms.map((t) => t?.term_start_date).filter(Boolean) : [];
        if (cancelled) return;
        if (courses.length > 0) setProgramOptions(courses);
        if (terms.length > 0) setTermDateOptions(sortTermLabelsChronologically(terms));
      } catch (e) {
        // Keep fallbacks on any error.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const todayKey = new Date().toDateString();
  const availableTermDates = useMemo(
    () => (termDateOptions || []).filter((label) => isTermDateOptionStillSelectable(label)),
    [todayKey, termDateOptions]
  );

  useEffect(() => {
    setForm((prev) => {
      if (!prev.term_start_date) return prev;
      if (availableTermDates.includes(prev.term_start_date)) return prev;
      return { ...prev, term_start_date: '' };
    });
  }, [availableTermDates]);

  useEffect(() => {
    setForm((prev) => {
      if (!prev.program) return prev;
      if ((programOptions || []).includes(prev.program)) return prev;
      return { ...prev, program: '' };
    });
  }, [programOptions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.student_name || !form.program || !form.term_start_date || !form.timezone) {
      setError('Please fill in all required fields (name, course, term date, timezone).');
      return;
    }

    if (form.availability_slots.length < 3) {
      setError(`Please select at least 3 one-hour slots. You currently have ${form.availability_slots.length} slot${form.availability_slots.length !== 1 ? 's' : ''}.`);
      return;
    }

    setStatus('submitting');

    try {
      const payload = {
        ...form,
        programs: form.program ? [form.program] : [],
        term_start_dates: form.term_start_date ? [form.term_start_date] : []
      };
      const res = await axios.post(`${API_BASE}/api/intake`, payload);
      if (res.data.matches && res.data.matches.length > 0) {
        setRequestId(res.data.request_id);
        setMatches(res.data.matches);
        setStatus('done');
      } else if (res.data.warning) {
        setError(`Matching issue: ${res.data.warning}. Check the dashboard for updates.`);
        setStatus('idle');
      } else {
        setStatus('done');
        setMatches([]);
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setStatus('idle');
    }
  };

  const handleReset = () => {
    setStatus('idle');
    setMatches(null);
    setError(null);
    setForm({
      student_name: '',
      parent_name: '',
      parent_email: '',
      program: '',
      term_start_date: '',
      timezone: '',
      availability_slots: [],
      comments: ''
    });
  };

  if (status === 'done' && matches && matches.length > 0) {
    return (
      <MatchResults
        matches={matches}
        studentName={form.student_name}
        requestId={requestId}
        onReset={handleReset}
        showCelebration
        termStartDates={form.term_start_date ? [form.term_start_date] : []}
        studentTimezone={form.timezone}
        program={form.program}
        initialAvailabilitySlots={form.availability_slots}
      />
    );
  }

  if (status === 'submitting') {
    return (
      <div className="submitting-screen">
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        <Loader size={48} color="var(--primary)" style={{ animation: 'spin 1s linear infinite', marginBottom: '1.5rem' }} />
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.75rem' }}>Finding the perfect coaches for your child …</h2>
        <p style={{ color: 'var(--gray-500)', lineHeight: 1.55, fontSize: '0.95rem' }}>
          Our AI agent is carefully reviewing scheduling availability and the best class fit to create a set of tailored matches for you. Hang tight! This will only take a few seconds
        </p>
      </div>
    );
  }

  const fieldStyle = {
    width: '100%',
    padding: '0.65rem 0.85rem',
    border: '1px solid var(--gray-300)',
    borderRadius: 8,
    fontSize: '0.95rem',
    fontFamily: 'inherit',
    color: '#111827',
    backgroundColor: '#fff',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  };

  return (
    <div className="intake-page">
      <div className="page-header">
        <h1 className="page-title">New Student Intake</h1>
        <p className="page-subtitle">Complete the form below. We'll match your student with coaches automatically.</p>
      </div>

      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8,
          padding: '0.75rem 1rem', marginBottom: '1.5rem', display: 'flex',
          alignItems: 'center', gap: '0.5rem', color: '#991b1b', fontSize: '0.875rem',
        }}>
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="card">
        <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

          {/* Student & Contact Information section */}
          <div className="form-section" style={{
            padding: '1.25rem',
            background: 'linear-gradient(to bottom, #faf9fb 0%, #fff 100%)',
            borderRadius: 12,
            border: '1px solid #ede9f2',
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a1a2e', marginBottom: '1rem', letterSpacing: '0.02em' }}>
              Student & Contact Information
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.9rem', color: '#374151' }}>
                  Student Full Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={form.student_name}
                  onChange={(e) => updateField('student_name', e.target.value)}
                  placeholder="e.g. Sarah Johnson"
                  style={fieldStyle}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.9rem', color: '#374151' }}>
                  Parent / Guardian Name
                </label>
                <input
                  type="text"
                  value={form.parent_name}
                  onChange={(e) => updateField('parent_name', e.target.value)}
                  placeholder="e.g. Jane Johnson"
                  style={fieldStyle}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.4rem', fontSize: '0.9rem', color: '#374151' }}>
                  Contact Email
                </label>
                <input
                  type="email"
                  value={form.parent_email}
                  onChange={(e) => updateField('parent_email', e.target.value)}
                  placeholder="e.g. jane@email.com"
                  style={fieldStyle}
                />
                <p style={{ marginTop: '0.35rem', fontSize: '0.8rem', color: '#6b7280', lineHeight: 1.4 }}>
                  We'll send booking confirmations and updates to this email.
                </p>
              </div>
            </div>
          </div>

          <SingleSelectRadioDropdown
            label="Course Selection *"
            placeholder="Select one course..."
            options={programOptions}
            value={form.program}
            onChange={(val) => updateField('program', val)}
          />

          <SingleSelectRadioDropdown
            label="Term Start Date *"
            placeholder="Select one term date..."
            options={availableTermDates}
            value={form.term_start_date}
            onChange={(val) => updateField('term_start_date', val)}
          />

          <SearchableTimezoneSelect
            label="Student Timezone *"
            value={form.timezone}
            onChange={(val) => updateField('timezone', val)}
          />

          <AvailabilityPicker
            slots={form.availability_slots}
            onChange={(slots) => updateField('availability_slots', slots)}
          />

          <div>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.35rem', fontSize: '0.9rem' }}>
              Additional Comments or Goals
            </label>
            <textarea
              value={form.comments}
              onChange={(e) => updateField('comments', e.target.value)}
              placeholder="Any specific goals, preferences, or notes for the matching team..."
              rows={3}
              style={{ ...fieldStyle, resize: 'vertical' }}
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary btn-lg"
            style={{ width: '100%', marginTop: '0.5rem' }}
          >
            <Plus size={20} />
            Submit & Find Coaches
          </button>
        </div>
      </form>
    </div>
  );
}